/**
 * Data source for the analytics pages.
 *
 * There is no analytics API yet — the backend's `GET /api/v1/analytics/dashboard`
 * is a stub answering `{ stats: {} }` — so this returns an empty snapshot and
 * leaderboard. It used to return invented figures, people (with stock photos)
 * and countries, shown to every user as their own organisation's analytics.
 * Replace with HTTP calls when the API lands; consumers go through
 * `analytics.service.ts`.
 */

export type AnalyticsRange = 'today' | 'yesterday' | 'lastWeek' | 'lastMonth'

export interface AnalyticsStats {
  live: number
  total: number
  minutes: string
  cancelled: number
  attendees: string
  recordings: number
  avgDuration: string
  changes: string[]
}

export interface AnalyticsInsightPoint {
  date: string
  meetings: number
  minutes: number
}

export interface AnalyticsTopUser {
  name: string
  role: string
  email: string
  timeSpent: number
}

export interface AnalyticsNamedValue {
  name: string
  value: number
}

export interface AnalyticsEngagement {
  emojis: number
  handRaise: number
  polls: number
}

export interface AnalyticsLocationRow {
  country: string
  meetings: number
}

export interface AnalyticsSnapshot {
  range: AnalyticsRange
  stats: AnalyticsStats
  insights: AnalyticsInsightPoint[]
  topUsers: AnalyticsTopUser[]
  features: AnalyticsNamedValue[]
  devices: AnalyticsNamedValue[]
  engagement: AnalyticsEngagement
  locations: AnalyticsLocationRow[]
}

const EMPTY_STATS: AnalyticsStats = {
  live: 0,
  total: 0,
  minutes: '0 mins',
  cancelled: 0,
  attendees: '0',
  recordings: 0,
  avgDuration: '0 mins',
  changes: [],
}

export interface AnalyticsLeaderboardEntry {
  rank: number
  name: string
  email: string
  meetings: number
  hours: number
  recordings: number
  avatar?: string
}

export const analyticsRepo = {
  async getSnapshot(range: AnalyticsRange): Promise<AnalyticsSnapshot> {
    return {
      range,
      stats: { ...EMPTY_STATS },
      insights: [],
      topUsers: [],
      features: [],
      devices: [],
      engagement: { emojis: 0, handRaise: 0, polls: 0 },
      locations: [],
    }
  },
  async getLeaderboard(): Promise<AnalyticsLeaderboardEntry[]> {
    return []
  },
}
